import { convert, LocalDate, nativeJs } from '@js-joda/core';
import { useEffect } from 'react';
import { useUpdateEffect } from 'react-use';

import { BindingCreator } from '@dreryk/react-common';
import { FormController } from '@dreryk/react-components';

import { useCalendarController, useVisibleCalendarDateSearchParam } from './CalendarController.hooks';
import { calendarPanelStorage } from './CalendarController.storage';
import {
   CalendarMode,
   CalendarSelectedDate,
   CalendarSelectedDateSetter,
   CalendarState,
} from './CalendarController.types';

type CalendarControllerRenderer = (renderProps: {
   mode: CalendarMode;
   currentDate: CalendarSelectedDate;
   setCurrentDate: CalendarSelectedDateSetter;
   register: BindingCreator<CalendarState>;
}) => JSX.Element;

type CalendarControllerProps = {
   readonly children: CalendarControllerRenderer;
   readonly forceSpecialistMode: boolean;
};

export const CalendarController = (props: CalendarControllerProps): JSX.Element => {
   const {
      getParam: getVisibleDateSearchParam,
      setParam: setVisibleDateSearchParam,
   } = useVisibleCalendarDateSearchParam();
   const { register, watch, setValue } = useCalendarController({
      forceSpecialistMode: props.forceSpecialistMode,
      initialCurrentDate: getVisibleDateSearchParam,
   });

   const { mode, currentDate } = watch(['mode', 'currentDate']);

   useUpdateEffect(() => {
      calendarPanelStorage.setMode(mode);
   }, [mode]);

   useEffect(() => {
      setVisibleDateSearchParam(currentDate);
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [currentDate]);

   return (
      <>
         <FormController controller={register('currentDate')} />
         {props.children({
            mode: mode,
            currentDate: currentDate,
            setCurrentDate: (date) => {
               const value = convert(LocalDate.from(nativeJs(date))).toDate();
               setValue('currentDate', value);
            },
            register: register,
         })}
      </>
   );
};
